/**
 * useMeDisplayName Hook
 *
 * Resolves the display name for the current "Me" user.
 * Priority: preferred name > member name > auth provider name > "Me"
 *
 * Also exposes `greetingName` — the first-name-only form used for the home
 * greeting. It's empty when no real name is available so the UI can render
 * "Hello!" instead of "Hello, Me!".
 */

import { useMemo, useState, useEffect } from 'react';
import { PreferredNameManager } from '@/lib/profile';
import { useSupabaseAuth } from '@/lib/supabase';
import { useFamilyMembers } from '@/hooks/use-family-members';
import { getFirstName, getAuthFullName } from '@/lib/profile/nameUtils';

type UseMeDisplayNameResult = {
  displayName: string;
  greetingName: string;
  isLoading: boolean;
};

export function useMeDisplayName(): UseMeDisplayNameResult {
  const { session } = useSupabaseAuth();
  const { meMember } = useFamilyMembers();
  const [preferredName, setPreferredName] = useState<string | null>(
    PreferredNameManager.getInstance().getPreferredName()
  );

  useEffect(() => {
    const manager = PreferredNameManager.getInstance();
    // Sync current value on mount (in case it was loaded before subscription)
    setPreferredName(manager.getPreferredName());

    // Subscribe to future changes (from StorageInitializer or profile edits)
    const unsubscribe = manager.subscribe(() => {
      setPreferredName(manager.getPreferredName());
    });
    return unsubscribe;
  }, []);

  const authFullName = getAuthFullName(session?.user?.user_metadata);

  // displayName intentionally treats meMember.name === "Me" as the default
  // placeholder (not a real name), so the auth first name takes precedence
  // over it. That way the family member card / profile card show "Andy"
  // instead of "Me" when the user has signed in with Google but hasn't yet
  // edited their preferred name.
  const displayName = useMemo(() => {
    if (preferredName) return preferredName;
    if (authFullName) return getFirstName(authFullName);
    if (meMember?.name && meMember.name !== 'Me') return meMember.name;
    return 'Me';
  }, [preferredName, authFullName, meMember?.name]);

  // greetingName mirrors displayName but returns an empty string when no real
  // name is available, so the greeting renders as "Hello!" rather than
  // "Hello, Me!".
  const greetingName = useMemo(() => {
    if (preferredName) return preferredName;
    if (authFullName) return getFirstName(authFullName);
    if (meMember?.name && meMember.name !== 'Me') return meMember.name;
    return '';
  }, [preferredName, authFullName, meMember?.name]);

  const isLoading = preferredName === null && !meMember?.name && !session?.user?.user_metadata;

  return { displayName, greetingName, isLoading };
}
